import { CLOSE_READING, RangeSelectionData } from '@mission.io/question-toolkit'
import { CloseAnnotationTypes, MarkdownTypes } from '@mission.io/question-views'
import { Position } from 'unist'

export type AvailableColor = keyof typeof CLOSE_READING.COLOR

export const AVAILABLE_COLORS: Array<AvailableColor> = Object.values(
	CLOSE_READING.COLOR
)

export const _COLOR_NAME_TO_HEX: Record<AvailableColor, string> = {
	[CLOSE_READING.COLOR.BLUE]: '#95effd', // use accent-blue from mission.io/styles game theme.
	[CLOSE_READING.COLOR.GREEN]: '#39fdc1', // use accent-green from mission.io/styles game theme.
	[CLOSE_READING.COLOR.ORANGE]: '#f7931e', // use accent-orange from mission.io/styles game theme.
	[CLOSE_READING.COLOR.YELLOW]: '#f6d365', // use a lightened prose.gold from mission.io/styles game theme.
	[CLOSE_READING.COLOR.PURPLE]: '#d4d7f7', // use prose.lavender from mission.io/styles game theme.
	[CLOSE_READING.COLOR.PINK]: '#ffd6cc', // use prose.coral from mission.io/styles game theme.
	[CLOSE_READING.COLOR.BLACK]: '#000000', // mission.io/styles black
}

export const COLOR_NAME_TO_HEX: Record<string, string> = _COLOR_NAME_TO_HEX

export const AVAILABLE_TOOLS: Array<keyof typeof CLOSE_READING.ANNOTATION_TOOL> = Object.values(
	CLOSE_READING.ANNOTATION_TOOL
)

export type ToolTypes = CloseAnnotationTypes.ToolTypes

export type Range = CloseAnnotationTypes.Range

export type HexColorString = string // a color in the form #rrggbb
export type AnnotationStyle = {
	tool: CloseAnnotationTypes.ToolTypes
	color: AvailableColor | HexColorString // usually AvailableColor but can also be a color in the form #rrbbgg
}

export type Annotation = {
	id: string
	range: Range
	style: AnnotationStyle
}

export type Annotations = Array<Annotation>

type RGB = { r: number; g: number; b: number }

const ErrorColor: RGB = {
	r: 129,
	g: 50,
	b: 50,
}
export const BUFFER_COLOR = '#FFC0CB'
const TEMPORARY_ID_PREFIX = 'SIM_CREATOR_ID_'

/**
 * getRangeIntersections - get the range representing the intersection of the two ranges
 *
 * @param range1 - get the intersection between this range and `range2`
 * @param range2 - get the intersection between this range and `range1`
 *
 * @returns {Range | null} the range of the intersection, or null if no intersection occurs
 */
export function getRangeIntersections(
	range1: Range,
	range2: Range
): Range | null {
	const maxStartIndex = Math.max(range1.start, range2.start)
	const minEndIndex = Math.min(range1.end, range2.end)

	if (maxStartIndex >= minEndIndex) {
		return null
	}

	return { start: maxStartIndex, end: minEndIndex }
}

/**
 * addAnnotation - add a new annotation to the list of the current annotations. If the added annotation overlaps with other annotations of the same type and color, then the
 * overlapping annotations will be merged into one annotation. This means that there will be no annotations of the same tool and color which overlap in the resulting annotations list.
 *
 * @param {Array<Annotation>} currentAnnotations - the current list of annotations
 * @param {Annotation} newAnnotation - the new annotation to add
 * @param {Range | null} rangeOverlappingNextSelectableElement - a range which covers the `newAnnotation` range and also covers the minimal amount of selectable ranges to either side of the `newAnnotation`. If null, will consider indices directly around `newAnnotation` as selectable indices. This is used to combine overlapping annotations and annotations right next to each other.
 * @param {number} textLength - the current length of the text the annotation is a part of
 *
 * @returns {Annotations} a new list of annotations
 */
export function addAnnotation<
	T extends {
		range: Range
		style?: {
			tool?: ToolTypes
			color?: AvailableColor | HexColorString
		}
	}
>(
	currentAnnotations: Array<T>,
	newAnnotation: T,
	rangeOverlappingNextSelectableElement: Range | null,
	textLength: number
): Array<T> {
	const disjointAnnotations = []
	const possibleCollisionAnnotations = []

	const extendedAnnotationRange: Range = rangeOverlappingNextSelectableElement ?? {
		// allows combining with other annotation of the same type
		start: Math.max(newAnnotation.range.start - 1, 0),
		end: Math.min(newAnnotation.range.end + 1, textLength),
	}
	for (const annotation of currentAnnotations) {
		if (
			annotation.style?.tool === newAnnotation.style?.tool &&
			annotation.style?.color === newAnnotation.style?.color &&
			getRangeIntersections(extendedAnnotationRange, annotation.range) !== null
		) {
			possibleCollisionAnnotations.push(annotation)
			continue
		}
		disjointAnnotations.push(annotation)
	}

	let currentRangeStart = newAnnotation.range.start
	let currentRangeEnd = newAnnotation.range.end
	for (const annotation of possibleCollisionAnnotations) {
		currentRangeStart = Math.min(currentRangeStart, annotation.range.start)
		currentRangeEnd = Math.max(currentRangeEnd, annotation.range.end)
	}
	disjointAnnotations.push({
		...newAnnotation,
		range: {
			start: currentRangeStart,
			end: currentRangeEnd,
		},
	})

	return disjointAnnotations
}

/**
 * deleteAnnotationRange - delete annotations of a given type and color in a given range
 *
 * @param {<T>{range: Range, style?: Style}} currentAnnotations - the current list of annotations
 * @param {<U>{range: Range, style?: Style}} rangeToDelete - an annotation describing the type and color of annotations to delete as well as the range to delete them from.
 *
 * @return {Array<T>} an updated list of annotations.
 */
export function deleteAnnotationRange<
	T extends {
		range: Range
		style?: {
			tool: ToolTypes | null
			color: AvailableColor | HexColorString | null
		}
	},
	U extends {
		range: Range
		style?: {
			tool: ToolTypes | null
			color: AvailableColor | HexColorString | null
		}
	}
>(currentAnnotations: Array<T>, rangeToDelete: U): Array<T> {
	const updatedAnnotations: Array<T> = []
	for (const annotation of currentAnnotations) {
		if (
			(!rangeToDelete.style?.tool ||
				annotation.style?.tool === rangeToDelete.style?.tool) &&
			(!rangeToDelete.style?.color ||
				annotation.style?.color === rangeToDelete.style?.color)
		) {
			const intersectionRange = getRangeIntersections(
				rangeToDelete.range,
				annotation.range
			)
			if (intersectionRange == null) {
				updatedAnnotations.push(annotation)
				continue
			}

			if (
				intersectionRange.start === annotation.range.start &&
				intersectionRange.end === annotation.range.end
			) {
				continue // the deletion range covered this entire annotation, remove it
			} else if (intersectionRange.start === annotation.range.start) {
				updatedAnnotations.push({
					...annotation,
					range: {
						...annotation.range,
						start: intersectionRange.end,
					},
				})
				continue
			} else if (intersectionRange.end === annotation.range.end) {
				updatedAnnotations.push({
					...annotation,
					range: {
						...annotation.range,
						end: intersectionRange.start,
					},
				})
				continue
			}

			// deletion range splits the current annotation
			updatedAnnotations.push({
				...annotation,
				range: {
					...annotation.range,
					end: intersectionRange.start,
				},
			})
			updatedAnnotations.push({
				...annotation,
				range: {
					...annotation.range,
					start: intersectionRange.end,
				},
			})
			continue
		}
		updatedAnnotations.push(annotation)
	}
	return updatedAnnotations
}

/**
 * deleteAnnotationsInRange - delete all annotations with the given type and color which intersect the given range
 *
 * @param {<T>{range: Range, style?: Style}} currentAnnotations - the current list of annotations
 * @param {<U>{range: Range, style?: Style}} rangeToDelete - an annotation describing the type and color of annotations to delete as well as the range to delete them from.
 *
 * @return {Array<T>} an updated list of annotations.
 */
export function deleteAnnotationsInRange<
	T extends {
		range: Range
		style?: {
			tool: ToolTypes | null
			color: AvailableColor | HexColorString | null
		}
	},
	U extends {
		range: Range
		style?: {
			tool: ToolTypes | null
			color: AvailableColor | HexColorString | null
		}
	}
>(currentAnnotations: Array<T>, rangeToDelete: U): Array<T> {
	return currentAnnotations.filter(({ range, style }) => {
		if (
			// matches erasing tool
			(!rangeToDelete.style?.tool ||
				style?.tool === rangeToDelete.style?.tool) &&
			// matches erasing color
			(!rangeToDelete.style?.color ||
				style?.color === rangeToDelete.style?.color)
		) {
			return !getRangeIntersections(range, rangeToDelete.range)
		}
		return true
	})
}

// an intermediate type used to denote where an annotation begins and end
type SplitAnnotation = {
	type: 'START' | 'END'
	index: number // where the annotation begins (inclusive) if type is 'START' or ends (exclusive: [ie. annotation does not style this index]) if type is 'END'
	baseAnnotation: Annotation // the annotation which starts or ends at this index
}

/**
 * combineAnnotationsIntoDisjointSections - creates a list of disjoint (non-overlapping) `RangedStyle` which describe all styles to apply within a range. This
 * makes it so each index (character) in the markdown will correspond to at most 1 RangedStyle, instead of possibly multiple annotations. The resulting RangedStyles
 * list is sorted by the startIndex (ascending).
 *
 * Example:       "Hello world this is a long sentence to demonstrate how this works."
 * Annotation 1:   |----------------underline--------------|
 * Annotation 2:                                 |------------highlight---------|
 * Becomes:
 * RangedStyle 1: |-----------underline----------|
 * RangedStyle 2:                                |---------| underline + highlight
 * RangedStyle 3:                                          |-----highlight------|
 *
 *
 * @param {Annotations} currentAnnotations - the list of annotations to create disjoint sections for
 *
 * @returns {RangedStyles} a list of disjoint RangeAnnotations which are sorted ascending by their start index
 */
export function combineAnnotationsIntoDisjointSections(
	currentAnnotations: Annotations
): MarkdownTypes.RangedStyles {
	const styleChangeLocations: Array<SplitAnnotation> = []
	for (const annotation of currentAnnotations) {
		styleChangeLocations.push(
			{
				type: 'START',
				index: annotation.range.start,
				baseAnnotation: annotation,
			},
			{
				type: 'END',
				index: annotation.range.end,
				baseAnnotation: annotation,
			}
		)
	}

	styleChangeLocations.sort((a, b) => a.index - b.index)

	const styleInCurrentRange = {
		startIndex: null as null | number,
		highlight: new Map<string, number>(),
		strikeThrough: new Map<string, number>(),
		underline: new Map<string, number>(),
	}

	const rangedStyles: Array<MarkdownTypes.RangedStyle> = []

	for (let i = 0; i < styleChangeLocations.length; i++) {
		const splitAnnotation = styleChangeLocations[i]
		const styleChangesAtSameIndex = [splitAnnotation]
		// get all annotations that start or end at the current markdown text index
		for (let j = i + 1; j < styleChangeLocations.length; j++) {
			// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
			if (styleChangeLocations[j].index === splitAnnotation.index) {
				styleChangesAtSameIndex.push(styleChangeLocations[j])
				i = j
			} else {
				// Note: `styleChangesAtSameIndex` is sorted on `index` (ascending), so there be no other SplitAnnotation with the wanted index at (and after) this point
				break
			}
		}

		if (styleInCurrentRange.startIndex != null) {
			// apply previous annotation styles until the start of this new range
			rangedStyles.push({
				range: {
					start: styleInCurrentRange.startIndex,
					// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
					end: splitAnnotation.index,
				},
				styles: {
					highlight: mixColors(
						Array.from(styleInCurrentRange.highlight.keys())
					),
					strikeThrough: mixColors(
						Array.from(styleInCurrentRange.strikeThrough.keys())
					),
					underline: mixColors(
						Array.from(styleInCurrentRange.underline.keys())
					),
				},
			})
		}

		// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
		styleInCurrentRange.startIndex = splitAnnotation.index

		for (const styleChange of styleChangesAtSameIndex) {
			// update styles in current annotation styles
			let setToChange = null
			if (
				// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				styleChange.baseAnnotation.style.tool ===
				CLOSE_READING.ANNOTATION_TOOL.HIGHLIGHT
			) {
				setToChange = styleInCurrentRange.highlight
			} else if (
				// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				styleChange.baseAnnotation.style.tool ===
				CLOSE_READING.ANNOTATION_TOOL.STRIKE_THROUGH
			) {
				setToChange = styleInCurrentRange.strikeThrough
			} else if (
				// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				styleChange.baseAnnotation.style.tool ===
				CLOSE_READING.ANNOTATION_TOOL.UNDERLINE
			) {
				setToChange = styleInCurrentRange.underline
			}

			if (setToChange == null) {
				continue
			}

			// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
			const changeAmount = styleChange.type === 'START' ? 1 : -1
			const updatedValue =
				// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				(setToChange.get(styleChange.baseAnnotation.style.color) ?? 0) +
				changeAmount
			if (updatedValue <= 0) {
				// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				setToChange.delete(styleChange.baseAnnotation.style.color)
				continue
			}
			// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
			setToChange.set(styleChange.baseAnnotation.style.color, updatedValue)
		}
	}

	return rangedStyles.filter(
		// remove ranges which apply no styles
		rangedStyle =>
			rangedStyle.styles.highlight != null ||
			rangedStyle.styles.strikeThrough != null ||
			rangedStyle.styles.underline != null
	)
}

/**
 * getNextId - get the next available id for an annotation in a list of annotations
 *
 * @param {Annotations} annotations -  the list of annotations already created with ids
 *
 * @returns {string} an id that is not currently being used in the list of annotations
 */
export function getNextId(annotations: Annotations): string {
	const usedIds = new Set()
	for (const annotation of annotations) {
		usedIds.add(annotation.id)
	}

	let counter = annotations.length
	let nextId = null
	do {
		counter += 1
		nextId = `${TEMPORARY_ID_PREFIX}${counter}`
	} while (usedIds.has(nextId))

	return nextId
}

/**
 * hexToRgb - converts an a hex color in the form of `#rrggbb` to a RGB object
 *
 * @param color - the hex string to convert
 *
 * @return {RGB} an object representation of the color
 */
export function hexToRgb(color: string): RGB {
	try {
		return {
			r: parseInt(color.slice(1, 3), 16) || 0, // `|| 0` to handle NaN
			g: parseInt(color.slice(3, 5), 16) || 0,
			b: parseInt(color.slice(5, 7), 16) || 0,
		}
	} catch (err) {
		return ErrorColor
	}
}

/**
 * mixColors - averages the colors supplied in the rgb colorspace
 *
 * @param {string[]} colors - either a color name or a color in the form `#rrggbb`
 *
 * @return {string | null} string representing a color in the form `#rrggbb`, null if no average could be determined
 */
export function mixColors(colors: string[]): string | null {
	if (!colors.length) {
		return null
	}

	const sum: RGB = {
		r: 0,
		g: 0,
		b: 0,
	}

	for (const colorString of colors) {
		const color = hexToRgb(COLOR_NAME_TO_HEX[colorString] ?? colorString)
		sum.r += color.r
		sum.g += color.g
		sum.b += color.b
	}

	const color: RGB = {
		r: Math.max(Math.min(Math.floor(sum.r / colors.length), 255), 0),
		g: Math.max(Math.min(Math.floor(sum.g / colors.length), 255), 0),
		b: Math.max(Math.min(Math.floor(sum.b / colors.length), 255), 0),
	}

	return `#${color.r.toString(16).padStart(2, '0')}${color.g
		.toString(16)
		.padStart(2, '0')}${color.b.toString(16).padStart(2, '0')}`
}

/**
 * getInlineCodeInformation - get the inline code language, codeSnippet, and position in the markdown
 *
 * @param {string} codeString - the plain code string from the InlineCode mdast node
 * @param {Position | null} position - the position in the markdown the InlineCode mdast node represents
 *
 * @return {{
 *   language: string, - the language the code snippet is for
 *   codeSnippet: string, - the text for the code snippet
 *   codeSnippetRange: Range | null, - where the code snippet is located in the markdown string
 * }}
 */
export function getInlineCodeInformation(
	codeString: string,
	position: Position | null
): { language: string; codeSnippet: string; codeSnippetRange: Range | null } {
	let languageString = 'code'
	let codeSnippet = codeString
	let codeSnippetRange =
		position?.start.offset != null && position?.end.offset
			? {
					start: position.start.offset + 1, // +1 to ignore the starting tick `
					end: position.end.offset - 1, // -1 to ignore the ending tick
			  }
			: null

	if (typeof codeString === 'string' && codeString.startsWith('lang-')) {
		const matches = codeString.match(/^lang-[^\s]*\s/)

		if (matches?.length) {
			languageString = matches[0].slice(
				0,
				// remove tailing space
				matches[0].length - 1
			)
			codeSnippet = codeString.slice(
				// ignore text describing the language to use
				languageString.length + 1,
				codeString.length
			)

			if (codeSnippetRange) {
				codeSnippetRange = {
					start: codeSnippetRange.start + languageString.length + 1,
					end: codeSnippetRange.end,
				}
			}
		}
	}

	const language = languageString?.replace('lang-', '')
	return {
		language,
		codeSnippet,
		codeSnippetRange,
	}
}

/**
 * doesRangeContainPoint - true if the given range contain the given point, false otherwise
 *
 * @param possibleParentRange - the range to check if the point is in
 * @param point - the point to check
 *
 * @return {boolean}
 */
export function doesRangeContainPoint(
	possibleParentRange: Range,
	point: number
): boolean {
	// Note: end is exclusive
	return possibleParentRange.start <= point && point < possibleParentRange.end
}

/**
 * getAnnotationWithStyleAlreadyCoveringRange - get the (first) annotation that covers the given range with the given style (if style is null, then it will get any annotation which covers the range)
 *
 * @param {Annotations} annotations - the annotations to look through
 * @param {Annotation} rangeAndStyle - the range and style to check for
 *
 * @return {Annotation} - the annotation which covers the given range with the given style
 */
export function getAnnotationWithStyleAlreadyCoveringRange<
	T extends { range: Range; style?: AnnotationStyle }
>(
	annotations: Array<T>,
	rangeAndStyle: { range: Range; style?: Partial<AnnotationStyle> }
): T | null {
	for (const annotation of annotations) {
		if (
			annotation.range.start <= rangeAndStyle.range.start &&
			rangeAndStyle.range.end <= annotation.range.end &&
			// only match style if a wanted style is given to match against
			(rangeAndStyle.style?.color == null ||
				rangeAndStyle.style.color === annotation.style?.color) &&
			(rangeAndStyle.style?.tool == null ||
				rangeAndStyle.style.tool === annotation.style?.tool)
		) {
			return annotation
		}
	}
	return null
}

/**
 * updateAnnotationRange - update the range of the annotation with the given id
 *
 * @param {string} annotationId - the id of the annotation to update the range for
 * @param {RangeSelectionData} rangeData - the new range for the annotation with the given id
 * @param {Annotations} currentAnnotations - the current annotations
 *
 * @return {{
 *   otherAnnotations: Annotations, - annotations which were not updated
 *   updatedAnnotation?: Annotation, - the updated annotation, null/undefined if the annotation with the given id did not exist
 * }}
 */
export function updateAnnotationRange(
	annotationId: string,
	rangeData: RangeSelectionData,
	currentAnnotations: Annotations
): { otherAnnotations: Annotations; updatedAnnotation?: Annotation } {
	let annotationBeingUpdated: Annotation | null = null
	const otherAnnotations: Annotations = []
	for (const annotation of currentAnnotations) {
		if (annotation.id === annotationId) {
			annotationBeingUpdated = annotation
			continue
		}
		otherAnnotations.push(annotation)
	}

	if (!annotationBeingUpdated) {
		return { otherAnnotations }
	}

	return {
		otherAnnotations,
		updatedAnnotation: { ...annotationBeingUpdated, range: rangeData.range },
	}
}
